const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export type CatalogItem = { id: number; name: string };

export type CatalogSuggestion = { id: string; displayName: string };

const MAX_SUGGESTIONS = 8;
const MAX_SKILLS = 4;
const MAX_JURISDICTIONS_PER_SKILL = 3;

export function buildCatalogSuggestions(
  query: string,
  skills: CatalogItem[],
  jurisdictions: CatalogItem[]
): CatalogSuggestion[] {
  const raw = query.trim();
  const q = normalize(raw);
  if (!q) return [];

  const parts = raw.split(/\s+en\s+/i);
  const skillPart = normalize(parts[0] ?? "");
  const jurisdictionPart = parts.length > 1 ? normalize(parts[1] ?? "") : "";

  const matchingSkills = skillPart
    ? skills
        .filter((s) => normalize(s.name).includes(skillPart))
        .slice(0, MAX_SKILLS)
    : skills.slice(0, MAX_SKILLS);

  const matchingJurisdictions = jurisdictionPart
    ? jurisdictions
        .filter((j) => normalize(j.name).includes(jurisdictionPart))
        .slice(0, MAX_JURISDICTIONS_PER_SKILL * 2)
    : jurisdictions.slice(0, MAX_JURISDICTIONS_PER_SKILL);

  const seen = new Set<string>();
  const result: CatalogSuggestion[] = [];

  for (const skill of matchingSkills) {
    if (result.length >= MAX_SUGGESTIONS) break;

    const skillLabel = skill.name;
    const jurisdictionSlice =
      matchingJurisdictions.length > 0
        ? matchingJurisdictions
        : jurisdictions.slice(0, MAX_JURISDICTIONS_PER_SKILL);

    for (const jur of jurisdictionSlice) {
      if (result.length >= MAX_SUGGESTIONS) break;
      const displayName = `${skillLabel} en ${jur.name}`;
      if (!seen.has(displayName)) {
        seen.add(displayName);
        result.push({ id: `cat-${skill.id}-${jur.id}`, displayName });
      }
    }

    if (result.length < MAX_SUGGESTIONS && !seen.has(skillLabel)) {
      seen.add(skillLabel);
      result.push({ id: `cat-${skill.id}`, displayName: skillLabel });
    }
  }

  return result.slice(0, MAX_SUGGESTIONS);
}
